import { Modal, Link } from '@atlassian/learning-components';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { FEEDBACK_PAGE_FF } from 'src/constants';
import { getLearningPathsHubPart } from 'src/utils/pathUtils';
import { getCatalogUrl, getCourseUrl, getLessonUrl, withPrefix } from 'src/utils/getUrl';
import {
  useCourseCompletionRate,
  useLearningPathCompletionRate,
  useProgressState,
} from 'src/store/progress/progressStore';
import { LessonLoaderProps } from 'src/pages/LessonPage/loader';
import { showConfetti } from 'src/pages/LessonPage/children/utils/confetti';
import { getSlug } from 'src/utils/slug';
import { findNextLessonSlug, getNextCourse } from 'src/pages/LessonPage/children/utils/nextCourseUtils';

interface BadgeModalProps {
  isLessonRecentlyCompleted: boolean;
  badgeImg: string;
}

const COMPLETED_RATE = 100;

enum ButtonLabel {
  FIND_YOUR_NEXT_COURSE = 'Find your next course',
  CONTINUE_TO_NEXT_COURSE = 'Continue to next course',
}

export const BadgeModal: React.FC<BadgeModalProps> = ({ isLessonRecentlyCompleted, badgeImg }) => {
  const { course, learningPath } = useLoaderData() as LessonLoaderProps;

  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const params = useParams();

  const [{ isLoadingProgressForCourses }] = useProgressState();
  const [courseCompletionRate] = useCourseCompletionRate(course);
  const [learningPathCompletionRate] = useLearningPathCompletionRate(learningPath);
  const [{ progressForCourses, progressForLessons }] = useProgressState();

  const isCourseModalRendered = useRef(false);
  const isPathModalRendered = useRef(false);

  const nextCourse = getNextCourse(learningPath, course, progressForCourses);
  const nextLessonSlug = findNextLessonSlug(nextCourse, progressForLessons);

  const isFeedbackPageAccessible = FeatureGates.checkGate(FEEDBACK_PAGE_FF);

  const isCourseCompleted = courseCompletionRate === COMPLETED_RATE;

  const shouldHide = !!(params?.pathId && !learningPath);

  const isLearningPathCompleted = learningPathCompletionRate === COMPLETED_RATE;

  useEffect(() => {
    if (!isLessonRecentlyCompleted) {
      isCourseModalRendered.current = false;
      isPathModalRendered.current = false;
    }
  }, [isLessonRecentlyCompleted]);

  useEffect(() => {
    if (isLoadingProgressForCourses || shouldHide) {
      return;
    }

    const isLearningPathCompletionModal =
      isLearningPathCompleted && isLessonRecentlyCompleted && !isPathModalRendered.current;

    const isCourseCompletionModal = isCourseCompleted && isLessonRecentlyCompleted && !isCourseModalRendered.current;

    if (isLearningPathCompletionModal || isCourseCompletionModal) {
      setIsOpen(true);
      showConfetti();
    }

    if (isCourseCompletionModal) {
      isCourseModalRendered.current = true;
    }

    if (isLearningPathCompletionModal) {
      isPathModalRendered.current = true;
    }
  }, [
    isLearningPathCompleted,
    isCourseCompleted,
    isLoadingProgressForCourses,
    isLessonRecentlyCompleted,
    shouldHide,
    learningPath,
  ]);

  const closeModal = useCallback(() => setIsOpen(false), []);

  const goToCatalog = useCallback(() => {
    setIsOpen(false);
    navigate(getCatalogUrl());
  }, [navigate]);

  const goToNextCourse = useCallback(() => {
    setIsOpen(false);

    navigate(
      getLessonUrl({
        pathSlug: getSlug(learningPath),
        courseSlug: nextCourse?.slug,
        lessonSlug: nextLessonSlug || '',
      }),
    );
  }, [learningPath, navigate, nextLessonSlug, nextCourse?.slug]);

  const generateButtonLabelForDialog = useMemo(() => {
    if (nextCourse && !isLearningPathCompleted) {
      return ButtonLabel.CONTINUE_TO_NEXT_COURSE;
    }

    return ButtonLabel.FIND_YOUR_NEXT_COURSE;
  }, [isLearningPathCompleted, nextCourse]);

  const handleRedirectForDialog = useMemo(() => {
    if (nextCourse && !isLearningPathCompleted) {
      return goToNextCourse;
    }

    return goToCatalog;
  }, [goToNextCourse, isLearningPathCompleted, nextCourse, goToCatalog]);

  if (!isOpen || shouldHide) {
    return null;
  }

  const courseSlug = course?.slug;
  const learningPathSlug = learningPath?.slug;
  const allLearningPathsHubPart = getLearningPathsHubPart(location.pathname);

  const surveyLink = withPrefix(getCourseUrl(courseSlug!, learningPathSlug, true), allLearningPathsHubPart);

  const surveyText = isFeedbackPageAccessible && (
    <p>
      Help us improve this course by taking a{' '}
      <Link label="short survey" link={surveyLink} onClick={() => window.open(surveyLink, '_blank', 'noopener')} />.
    </p>
  );

  if (isLearningPathCompleted) {
    return (
      <ModalTransition>
        <Modal
          heading="Congrats"
          subheading={<p>You earned a course badge, and completed the learning path {learningPath?.title}!</p>}
          surveyText={surveyText}
          imageLink={badgeImg}
          closeModal={closeModal}
          actionButton={{
            label: generateButtonLabelForDialog,
            onClick: handleRedirectForDialog,
          }}
          width={720}
        />
      </ModalTransition>
    );
  }

  return (
    <ModalTransition>
      <Modal
        heading="You earned a badge!"
        subheading={
          <p>Great work! You earned a badge by completing this course. All badges are saved in your profile.</p>
        }
        surveyText={surveyText}
        imageLink={badgeImg}
        closeModal={closeModal}
        actionButton={{
          label: generateButtonLabelForDialog,
          onClick: handleRedirectForDialog,
        }}
        width={720}
      />
    </ModalTransition>
  );
};
